import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import humanFormat from 'human-format';
import { recordStatisticsEvent } from '../../../services/statistics-service';
import StyledTooltip from './styled-tooltip';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import { like, unlike } from '../../../services/wire/like-service';
import Button from '../shared/styles';
import { subscribe, disconnect } from '../../../channels/pub-sub-service';

const getNumber = (c, isCompact) => {
  if (!c) {
    return '\u00A0';
  }
  return humanFormat(c, { decimals: 1, separator: isCompact ? '' : ' ' });
};

export default function Like({
  wire, asButton, showLabel, isOnROIPage, position,
}) {
  const [pressed, setPressed] = React.useState(false);
  const [count, setCount] = React.useState(wire.likeCount);
  const [liked, setLiked] = React.useState(wire.liked);
  const tooltipTitle = liked ? 'Unlike this wire' : 'Like this wire';

  React.useEffect(() => () => disconnect(), []);

  React.useEffect(() => {
    wire.id && subscribe(
      { channel: 'WireStatisticChannel', id: wire.id },
      {
        received: ({ likeCount }) => {
          if (Number.isInteger(likeCount)) {
            setCount(likeCount);
            likeCount || setLiked(false);
          }
        },
      },
    );
  }, [wire.id]);

  React.useEffect(() => {
    setLiked(wire.liked);
    setCount(wire.likeCount);
  }, [wire]);

  const toggle = e => {
    e && e.preventDefault();

    if (!window.Livewire.currentUser) {
      window.location = '/sign_up';
      return;
    }

    (liked ? unlike : like)(wire.id, !isOnROIPage)
      .then(result => {
        setLiked(!liked);
        setCount(result.data.like_count);
        if (isOnROIPage) {
          recordStatisticsEvent({
            event: liked ? 'unlike_created' : 'like_created',
            statisticable: 'wire',
            id: wire.id,
            clicked_url: window.location.pathname,
            page: window.location.pathname,
            custom_data: {
              section: 'all_wires',
              page: 'series',
              position,
            },
          });
        }
      })
      .catch(error => defaultOnErrorHandler(error, 'Cannot toggle like status'));
  };

  const iconCls = {
    'icon-lw-wire-liked': liked,
    'icon-lw-wire-like': !liked,
  };

  const num = getNumber(count, asButton);

  if (asButton) {
    return (
      <StyledTooltip
        disableTouchListener
        title={tooltipTitle}
        enterDelay={300}
      >
        <Button
          className={classNames(`btn ${isOnROIPage ? 'wire-like' : ''} `, { pressed })}
          type="button"
          onClick={toggle}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
        >
          <span className={classNames('icon-lw', iconCls)} />
          {!!count && <span className="number">{num}</span>}
          {showLabel && <span className="text">{count < 2 ? 'like' : 'likes'}</span>}
        </Button>
      </StyledTooltip>
    );
  }
  return (
    <>
      <span className="count">{count ? humanFormat(count, { decimals: 1 }) : '\u00A0'}</span>
      <a href="#" onClick={toggle} data-id="like-link">
        <span className={classNames('icon-lw', iconCls)} />
      </a>
    </>
  );
}

Like.propTypes = {
  wire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    liked: PropTypes.bool.isRequired,
    likeCount: PropTypes.number.isRequired,
  }).isRequired,
  asButton: PropTypes.bool,
  showLabel: PropTypes.bool,
  isOnROIPage: PropTypes.bool,
  position: PropTypes.number,
};

Like.defaultProps = {
  asButton: false,
  showLabel: false,
  isOnROIPage: false,
  position: null,
};
